import { useState, useEffect, useCallback } from 'react';
import { debounce, each, omitBy } from 'lodash';
import dayjs from 'dayjs';
import sanitizeHtml from 'sanitize-html';

import { MAP_ROWS, MAP_GROUP } from 'modules/transactions/constant';
import { toastError, toastSuccess } from 'partial/components/react-geek-toast';
import AuthStore from 'auth-store';

export const aclHelper = (mod, access, element) => {
  const permissions = JSON.parse(AuthStore.get('_permissions'));
  if (permissions?.[mod] && permissions?.[mod]?.indexOf(access) !== -1)
    return element || true;
  return element ? null : false;
};

export const intToString = (num) => {
  const number = num.toString().replace(/[^0-9.]/g, '');
  if (number < 1000) {
    return number;
  }
  const si = [
    { v: 1e3, s: 'K' },
    { v: 1e6, s: 'M' },
    { v: 1e9, s: 'B' },
    { v: 1e12, s: 'T' },
    { v: 1e15, s: 'P' },
    { v: 1e18, s: 'E' },
  ];
  let index;
  // eslint-disable-next-line
  for (index = si.length - 1; index > 0; index--) {
    if (number >= si[index].v) {
      break;
    }
  }
  return (
    (number / si[index].v)
      .toFixed(2)
      .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[index].s
  );
};

export const removeEmpty = (obj) => omitBy(obj, (x) => x === '');

export const isJson = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const storage = {
  get: (key, defaultValue = false) => {
    try {
      return JSON.parse(sessionStorage.getItem(key));
    } catch (err) {
      return defaultValue;
    }
  },
  set: (key, value) => {
    const newValue = JSON.stringify(value);
    sessionStorage.setItem(key, newValue);
  },
  remove: (key) => {
    sessionStorage.removeItem(key);
  },
};

export const parseNumber = (str, defaultValue = false) => {
  const v = parseFloat(`${str}`.replace(/,/g, ''));
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(v)) return typeof defaultValue !== 'boolean' ? defaultValue : str;
  return v;
};

export const formatNumber = (v, decimal = 2) => {
  try {
    const n = parseNumber(v);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  } catch (err) {
    return v;
  }
};

export const usePersistState = (
  key,
  state,
  config = { keepOnUnmount: true },
) => {
  const [value, setValue] = useState(storage.get(key) || state);
  // eslint-disable-next-line
  const updateState = useCallback(
    debounce((newState) => {
      storage.set(key, newState);
    }, 300),
    [],
  );
  useEffect(() => {
    updateState(value);
  }, [value]); // eslint-disable-line
  // eslint-disable-next-line
  useEffect(() => {
    return () => {
      if (!config.keepOnUnmount) {
        storage.remove(key);
      }
    };
  }, []); // eslint-disable-line
  return [value, setValue];
};

export const formatDate = (date, format = 'MM/DD/YYYY', defaultValue = '-') => {
  if (!date) return defaultValue;
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return defaultValue;
  return dayjs(d).format(format);
};

const formatRowKey = (key, value) =>
  ({
    hour: formatDate(value?.transaction_date_time, 'hh:mm A'),
    day: formatDate(value?.transaction_date_time, 'D'),
    month: formatDate(value?.transaction_date_time, 'MMM'),
  }[key]);

export const transformCompareTable = (mode) => (raw) => {
  const column = {};
  const key = MAP_GROUP[mode];

  each(raw?.data, (row, id) => {
    const formatted = {
      id,
      label: row?.transaction_date_time,
      transaction: parseNumber(row?.transaction_count),
      transaction_human: row?.transaction_count,
      amount: parseNumber(row?.transaction_collection),
      amount_human: row?.transaction_collection,
    };

    const rowKey = formatRowKey(key, row);

    if (!rowKey) console.warn('Unregistered row key!', key);

    column[rowKey] = formatted;
  });

  return column;
};

const ID_BY_MODE = {
  day: 'hh:mm A',
  month: 'D',
  year: 'MMM',
};

export const transformCompareChart = (mode /* params */) => (raw) => {
  const list = [];
  const rows = MAP_ROWS[mode] || [];
  const data = {};
  each(raw?.data, (row, id) => {
    const formatted = {
      id,
      label: row?.transaction_date_time,
      transaction: parseNumber(row?.transaction_count),
      transaction_human: row?.transaction_count,
      amount: parseNumber(row?.transaction_collection),
      amount_human: row?.transaction_collection,
    };
    data[formatDate(row?.transaction_date_time, ID_BY_MODE[mode])] = formatted;
  });

  each(rows, (label, id) => {
    list.push({
      amount: null,
      amount_human: '-',
      transaction: null,
      transaction_human: '-',
      ...(data[label] || {}),
      label,
      id,
    });
  });

  return list;
};

export const downloadArrayToCsv = (
  csvData,
  filename,
  onSuccess = () => {},
  onError = () => {},
) => {
  try {
    const newCsvData = csvData.replaceAll('#', '');
    const csvContent = `data:text/csv;charset=utf-8,${newCsvData}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onSuccess();
    toastSuccess('Successfully exported logs.');
  } catch (err) {
    onError();
    toastError('Unable to generate download.');
  }
};

export const sanitizer = (data) => {
  const cleanData = sanitizeHtml(data, {
    allowedTags: [
      'br',
      'b',
      'i',
      'em',
      'strong',
      'normal',
      'a',
      'p',
      'ul',
      'li',
      'ol',
      'div',
      'img',
      'span',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'sup',
      'sub',
      'ins',
      'del',
      'code',
      'figure',
      'blockquote',
      'table',
      'tbody',
      'tr',
      'td',
      'th',
      'thead',
      'dd',
      'dt',
      'figcaption',
      's',
      'i',
      'u',
      'mark',
      'oembed',
      'iframe',
    ],
    selfClosing: ['img', 'br'],
    allowedAttributes: {
      a: ['href', 'target', 'class'],
      img: ['src', 'style', 'class'],
      p: ['class', 'style'],
      span: ['class', 'style'],
      ol: ['class', 'style'],
      ul: ['class', 'style'],
      li: ['class', 'style'],
      blockquote: ['class', 'style'],
      table: ['class', 'style'],
      tbody: ['class', 'style'],
      tr: ['class', 'style'],
      td: ['class', 'style'],
      th: ['class', 'style'],
      thead: ['class', 'style'],
      dd: ['class', 'style'],
      dt: ['class', 'style'],
      figure: ['class', 'style', 'url'],
      figcaption: ['class', 'style'],
      mark: ['class', 'style'],
      oembed: ['url', 'class', 'figure'],
      div: ['class', 'style', 'data-oembed-url', 'oembed'],
      iframe: [
        'class',
        'style',
        'frameborder',
        'src',
        'allow',
        'allowfullscreen',
      ],
    },
  });

  return cleanData;
};
